/**
 * Mietverträge-Tab helpers — Formatierung + Link-Bau für den
 * Mietverträge-Tab (Tab 5) im Fahrzeug-Detail-Drawer.
 *
 * Fahrzeug.mietvertrag_akte_id verweist auf die Akte, in der der
 * Unfallersatz-Mietvertrag geführt wird. Wenn gesetzt, wird ein Cross-Link
 * mit Vertragslaufzeit, Mieter und Status angezeigt; Klick öffnet die Akte
 * in neuem Tab. Ohne Link: Leer-State.
 */

import { aktenIdentifier, buildAkteHref } from './akten-link';

export interface MietvertragAkte {
  id: string;
  akten_nr: string | null;
  akten_nummer: string | null;
  business_unit: string;
  kunde_name: string | null;
  schadendatum: string | null;
  status: string | null;
  mietbeginn: string | null;
  mietende: string | null;
}

const EM_DASH = '—';

const STATUS_LABELS: Record<string, string> = {
  neu: 'Neu',
  in_bearbeitung: 'In Bearbeitung',
  warten: 'Wartend',
  abgeschlossen: 'Abgeschlossen',
  storniert: 'Storniert',
};

function formatDateDE(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = d.getUTCFullYear();
  return `${dd}.${mm}.${yyyy}`;
}

/**
 * Baut die Anzeige für die Vertragslaufzeit. Akzeptiert partielle Daten
 * (nur Start, nur Ende, beides, keins) und ignoriert ungültige Datumsangaben.
 */
export function formatLaufzeit(
  start: string | null | undefined,
  ende: string | null | undefined,
): string {
  const s = formatDateDE(start);
  const e = formatDateDE(ende);
  if (s && e) return `${s} – ${e}`;
  if (s) return `seit ${s}`;
  if (e) return `bis ${e}`;
  return EM_DASH;
}

export function getMietvertragStatusLabel(status: string | null | undefined): string {
  if (!status) return EM_DASH;
  return STATUS_LABELS[status] ?? status;
}

/**
 * Baut die URL, auf die der Klick auf den Mietvertrag navigiert. Verwendet
 * denselben Identifier-Fallback wie `buildAkteHref` (akten_nummer vor
 * akten_nr). Gibt `null` zurück, wenn keine Akten-Nummer verfügbar ist —
 * dann darf die UI keinen Link rendern.
 */
export function buildMietvertragHref(akte: MietvertragAkte): string | null {
  const ident = aktenIdentifier(akte);
  if (!ident) return null;
  return buildAkteHref(akte.business_unit, ident);
}
